import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../services/firebaseAuth";
import { useAppSelector } from "../../store";
import { initializeNotifications } from "../../features/notifications/notificationSlice";
import {
  fetchPlayersWithRegistrations,
  getPlayersWithRegistrationsLoading,
} from "../../features/player/playerSlice";
import PlayerFollowList from "./PlayerFollowList";
import NotificationSettings from "./NotificationSettings";
import PushNotificationBlock from "./PushNotificationBlock";
import EmailNotificationBlock from "./EmailNotificationBlock";
import { useAppDispatch } from "../../store";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`notification-tabpanel-${index}`}
      aria-labelledby={`notification-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const PlayerFollowPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [user, userLoading] = useAuthState(auth);
  const { isInitialized, loading, error } = useAppSelector(
    (state) => state.notifications
  );
  const { playersWithRegistrations } = useAppSelector((state) => state.player);
  const playersLoading = useAppSelector(getPlayersWithRegistrationsLoading);
  console.log("🔍 [DEBUG] playersWithRegistrations", playersWithRegistrations);
  console.log("🔍 [DEBUG] playersLoading", playersLoading);

  const [tabValue, setTabValue] = useState(0);
  const [playersList, setPlayersList] = useState<any[]>([]);

  // Initialiser les notifications si l'utilisateur est connecté
  useEffect(() => {
    if (user?.uid && !isInitialized) {
      dispatch(initializeNotifications(user.uid));
    }
  }, [dispatch, user?.uid, isInitialized]);

  // Charger la liste des joueurs
  useEffect(() => {
    if (playersWithRegistrations && playersWithRegistrations.length > 0) {
      setPlayersList(playersWithRegistrations);
    }
  }, [playersWithRegistrations]);

  // Charger les joueurs si pas encore chargés
  useEffect(() => {
    if (playersWithRegistrations.length === 0 && !playersLoading) {
      console.log("🔄 Chargement des joueurs inscrits...");
      dispatch(fetchPlayersWithRegistrations());
    }
  }, [dispatch, playersWithRegistrations.length, playersLoading]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (userLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ py: 8 }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">
          Vous devez être connecté pour accéder aux notifications.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Notifications et suivi de joueurs
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configurez vos préférences de notifications et suivez vos joueurs
          favoris
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading && !isInitialized ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ py: 8 }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Blocs de configuration des notifications */}
          <Box sx={{ mb: 4 }}>
            <PushNotificationBlock userId={user.uid} />
            <EmailNotificationBlock userId={user.uid} />
          </Box>

          <Box sx={{ width: "100%" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="notification tabs"
              >
                <Tab label="Joueurs suivis" />
                <Tab label="Paramètres" />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              {playersLoading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ py: 4 }}
                >
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>
                    Chargement des joueurs...
                  </Typography>
                </Box>
              ) : playersList.length === 0 ? (
                <Alert severity="info">
                  Aucun joueur inscrit disponible. Veuillez patienter pendant le
                  chargement.
                </Alert>
              ) : (
                <PlayerFollowList players={playersList} userId={user.uid} />
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <NotificationSettings userId={user.uid} />
            </TabPanel>
          </Box>
        </>
      )}
    </Container>
  );
};

export default PlayerFollowPage;
